import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type { DictionaryOptionItem } from './types/dictionary';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm';

export type PageResult<T> = {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
};

export type OptionTypeKind = 'simple' | 'domain_extended' | 'system_enum';

export type OptionTypeRecord = {
  id: number;
  uuid: string;
  appCode: string;
  code: string;
  name: string;
  categoryCode?: string;
  scope: string;
  typeKind: OptionTypeKind;
  extensionTable?: string;
  editable: boolean;
  deletable: boolean;
  enabled: boolean;
  builtIn: boolean;
  description?: string;
  createTime?: string;
  updateTime?: string;
  itemCount?: number;
};

export type OptionItemRecord = {
  id: number;
  uuid: string;
  optionTypeUuid: string;
  appCode: string;
  typeCode: string;
  code: string;
  label: string;
  value?: string;
  color?: string;
  icon?: string;
  sortOrder: number;
  enabled: boolean;
  builtIn: boolean;
  editable: boolean;
  deletable: boolean;
  isDefault: boolean;
  parentItemUuid?: string;
  description?: string;
  createTime?: string;
  updateTime?: string;
};

export type GeneratedOptionItem = {
  label: string;
  code: string;
  color?: string;
  icon?: string;
  sortOrder: number;
  enabled?: number;
  description?: string;
};

export type ChatModelReadiness = {
  ready: boolean;
  appId: string;
  hasAppDefaultModel: boolean;
  hasSystemDefaultModel: boolean;
  hasSelectableModels: boolean;
};

export type OptionSelectOption = {
  uuid: string;
  value: string;
  code?: string;
  label: string;
  color?: string;
  icon?: string;
  parentItemUuid?: string;
  description?: string;
};

export type OptionItemPageResult = PageResult<OptionItemRecord> & {
  type: OptionTypeRecord;
};

export type SalesPipelineRecord = {
  id: number;
  uuid: string;
  code?: string;
  name: string;
  description?: string;
  isDefault: number;
  enabled: number;
  sortOrder: number;
  createTime?: string;
  updateTime?: string;
};

export type OpportunityStageSettingRecord = {
  uuid: string;
  optionItemUuid: string;
  pipelineUuid: string;
  pipelineName: string;
  code?: string;
  label: string;
  color?: string;
  icon?: string;
  sortOrder: number;
  enabled: boolean;
  probability: number;
  stageType: 'normal' | 'won' | 'lost';
  isWon: boolean;
  isLost: boolean;
  allowJump: boolean;
  definition?: string;
  requiredFieldsJson?: string;
  entryCriteria?: string;
  exitCriteria?: string;
  maxStayDays?: number;
  requiredActions?: string;
};

export type QueryOptionTypesParams = {
  page?: number;
  pageSize?: number;
  keyword?: string;
  appCode?: string;
  categoryCode?: string;
};

export type QueryOptionItemsParams = {
  page?: number;
  pageSize?: number;
  keyword?: string;
};

export type QuerySalesPipelinesParams = QueryOptionItemsParams;

export type QueryOpportunityStageSettingsParams = QueryOptionItemsParams & {
  pipelineUuid?: string;
};

const readArrayPayload = <T>(resp: any): T[] => {
  const candidates = [
    resp,
    resp?.list,
    resp?.items,
    resp?.data,
    resp?.data?.list,
    resp?.data?.items,
    resp?.result,
    resp?.result?.list,
    resp?.result?.items,
    resp?.payload,
    resp?.payload?.list,
    resp?.payload?.items,
  ];
  const matched = candidates.find((item) => Array.isArray(item));
  return Array.isArray(matched) ? matched : [];
};

export async function queryOptionTypes(
  params: QueryOptionTypesParams,
  options?: ApiRequestOptions,
) {
  return request<PageResult<OptionTypeRecord>>(`${BASE_PATH}/option-types`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createOptionType(
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<OptionTypeRecord>(`${BASE_PATH}/option-types`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateOptionType(
  uuid: string,
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<OptionTypeRecord>(`${BASE_PATH}/option-types/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteOptionType(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/option-types/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function queryOptionItems(
  typeCode: string,
  params: QueryOptionItemsParams,
  options?: ApiRequestOptions,
) {
  return request<OptionItemPageResult>(`${BASE_PATH}/option-types/${typeCode}/items`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createOptionItem(
  typeCode: string,
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<OptionItemRecord>(`${BASE_PATH}/option-types/${typeCode}/items`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function generateOptionItemCode(
  typeCode: string,
  payload: { label?: string },
  options?: ApiRequestOptions,
) {
  return request<{ code: string }>(
    `${BASE_PATH}/option-types/${typeCode}/items/generate-code`,
    {
      method: 'POST',
      data: payload,
      ...(options || {}),
    },
  );
}

export async function generateOptionItemBatch(
  typeCode: string,
  payload: { labels?: string[] },
  options?: ApiRequestOptions,
) {
  return request<{ items: GeneratedOptionItem[] }>(
    `${BASE_PATH}/option-types/${typeCode}/items/generate-batch`,
    {
      method: 'POST',
      data: payload,
      ...(options || {}),
    },
  );
}

export async function queryChatModelReadiness(options?: ApiRequestOptions) {
  return request<ChatModelReadiness>(`${BASE_PATH}/ai/chat-model-readiness`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function updateOptionItem(
  typeCode: string,
  uuid: string,
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<OptionItemRecord>(`${BASE_PATH}/option-types/${typeCode}/items/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteOptionItem(
  typeCode: string,
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/option-types/${typeCode}/items/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function queryOptionItemOptions(
  typeCode: string,
  keyword?: string,
  options?: ApiRequestOptions,
) {
  const resp = await request<any>(
    `${BASE_PATH}/option-items/options/${typeCode}`,
    {
      method: 'GET',
      params: keyword ? { keyword } : undefined,
      ...(options || {}),
    },
  );
  return readArrayPayload<OptionSelectOption>(resp);
}

export async function queryDictionaryItemOptions(
  code: string,
  keyword?: string,
  options?: ApiRequestOptions,
) {
  return request<DictionaryOptionItem[]>(`${BASE_PATH}/dictionary-options/${code}`, {
    method: 'GET',
    params: keyword ? { keyword } : undefined,
    ...(options || {}),
  });
}

export async function querySalesPipelines(
  params: QuerySalesPipelinesParams,
  options?: ApiRequestOptions,
) {
  return request<PageResult<SalesPipelineRecord>>(`${BASE_PATH}/sales-pipelines`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function createSalesPipeline(
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<SalesPipelineRecord>(`${BASE_PATH}/sales-pipelines`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateSalesPipeline(
  uuid: string,
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<SalesPipelineRecord>(`${BASE_PATH}/sales-pipelines/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteSalesPipeline(uuid: string, options?: ApiRequestOptions) {
  return request<void>(`${BASE_PATH}/sales-pipelines/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export async function querySalesPipelineOptions(
  keyword?: string,
  options?: ApiRequestOptions,
) {
  return request<OptionSelectOption[]>(`${BASE_PATH}/sales-pipeline-options`, {
    method: 'GET',
    params: keyword ? { keyword } : undefined,
    ...(options || {}),
  });
}

export async function queryOpportunityStageSettings(
  params: QueryOpportunityStageSettingsParams,
  options?: ApiRequestOptions,
) {
  return request<PageResult<OpportunityStageSettingRecord>>(
    `${BASE_PATH}/opportunity-stage-settings`,
    {
      method: 'GET',
      params,
      ...(options || {}),
    },
  );
}

export async function createOpportunityStageSetting(
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/opportunity-stage-settings`, {
    method: 'POST',
    data: payload,
    ...(options || {}),
  });
}

export async function updateOpportunityStageSetting(
  uuid: string,
  payload: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request(`${BASE_PATH}/opportunity-stage-settings/${uuid}`, {
    method: 'PATCH',
    data: payload,
    ...(options || {}),
  });
}

export async function deleteOpportunityStageSetting(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/opportunity-stage-settings/${uuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}
